import { createServerOrPathString } from '@tableau/data-catalog-client-components';
import { ButtonType, ButtonWidget, HorizontalOriginOption, RadioButtonWithLabelWidget, TabStyles, TextLinkWidget, VerticalOriginOption } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { Messages } from '../../../codegen/Localize';
import { ButtonWithUberDialog } from '../../../common/react/components/ButtonWithUberDialog';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { HelpService } from '../../../commonInfra/services/HelpService';
import { boxStyle, cssClass, flexRowStyle } from '../../../style';
import { ReactActionDialogBodyProps } from '../../../commonInfra/dialogs/ReactActionDialog';
import { DatabaseForGrouping } from '../../actions/spaces/GroupDatabasesAction';
import { mapStateToSiteUrlsProps, SiteUrlsProps } from '../../selectors/SiteUrlsSelectors';
import { Telemetry, ValidTelemetryEvent } from '../../../commonInfra/services/Telemetry';

export type GroupDatabasesDialogBodyProps = { readonly databases: ReadonlyArray<DatabaseForGrouping> };
export type GroupDatabasesDialogState = { readonly primaryDatabaseLuid: string };
export type PropsForConfirmActionDialogBody = ReactActionDialogBodyProps<GroupDatabasesDialogBodyProps, GroupDatabasesDialogState>;

export const ConnectedGroupDatabasesDialogBody: React.ComponentType<PropsForConfirmActionDialogBody> = connect(mapStateToSiteUrlsProps)(GroupDatabasesDialogBody);

export const DatabaseRadioButtonRowTestId = 'database-radio-button-row';

enum GroupDatabasesDialogTelemetryEvents {
  clickedInfoButton = 'GroupDatabasesDialog_ClickedInfoButton'
}

interface GroupDatabasesDialogInfoClickedEvent
extends ValidTelemetryEvent<GroupDatabasesDialogInfoClickedEvent> {
  type: GroupDatabasesDialogTelemetryEvents.clickedInfoButton;
  databaseId: string;
}

export function GroupDatabasesDialogBody(props: PropsForConfirmActionDialogBody & SiteUrlsProps): JSX.Element {

  const setPrimaryDatabase = React.useCallback(
    (value: string) => props.updateDialogState({ primaryDatabaseLuid: value }),
    [props.updateDialogState]
  );

  const selectedRadioButtonIndex = props.databases.findIndex(d => d.luid === props.dialogState.primaryDatabaseLuid);

  return (
    <div>
      <div className={explanatoryParagraphClass}>
        {Messages.groupDatabases_dialogBodyText1()}
        <span className={inlineButtonContainerClass}>
          <ButtonWithUberDialog
            renderButton={showDialog => (
              <ButtonWidget
                testId="group-databases-dialog-info-button"
                buttonType={ButtonType.Icon}
                onClick={showDialog}
                title={Messages.groupDatabases_moreInfo()}
              >
                <img src={imgPath('data_prep_info.svg')} alt={Messages.groupDatabases_moreInfo()} />
              </ButtonWidget>
            )}
            uberDialogContent={<ExtraInfoUberDialogContent/>}
            anchorOrigin={{ horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.centerY }}
            stackedOrigin={{ horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.bottom }}
          />
        </span>
      </div>
      <div className={explanatoryParagraphClass}>
        {Messages.groupDatabases_dialogBodyText2()}
      </div>

      <section className={scrollableRadioButtonsAreaClass}>
        {props.databases.map((database, i) => (
          <div
            className={radioButtonRowClass}
            key={database.luid}
            data-tb-test-id={DatabaseRadioButtonRowTestId}
          >
            <RadioButtonWithLabelWidget
              value={database.luid}
              index={i}
              label={null}
              testId={`database-radio-button-${database.luid}`}
              checked={i === selectedRadioButtonIndex}
              handleChange={setPrimaryDatabase}
              groupName="group-databases-radio-buttons"
            >
              {addSoftHyphens(createServerOrPathString(database))}
              <span className={radioButtonRowSpacerClass} />
              <TextLinkWidget
                testId="open-database-in-new-tab-link"
                href={props.urls.catalogDatabaseUrl(database.id)}
                target="_blank" rel="noopener noreferrer"
                onClick={() => recordOpenDatabaseEvent(database.id)}
              >
                <div className={externalLinkIconClass}>
                  <img
                    src={imgPath('goto_generic_10px_xxsmall.svg')}
                    alt={Messages.groupDatabases_linkTitle()}
                    title={Messages.groupDatabases_linkTitle()}
                  />
                </div>
              </TextLinkWidget>
            </RadioButtonWithLabelWidget>
          </div>
        ))}
      </section>
    </div>
  );
}

function recordOpenDatabaseEvent(databaseId: string): void {
  // Record a telemetry event that the database's detail link has been clicked.
  Telemetry.sendEvent<GroupDatabasesDialogInfoClickedEvent>({
    type: GroupDatabasesDialogTelemetryEvents.clickedInfoButton,
    databaseId: databaseId,
  });
}

// This function is exported so that the tests can use it.
export function addSoftHyphens(input: string): string {
  // Add a soft hyphen (U+00AD) after each forward and backward slash in the input.
  return input.replace(new RegExp(/\//g), '/\xad').replace(new RegExp(/\\/g), '\\\xad');
}

function ExtraInfoUberDialogContent() {
  const learnMoreDialogUrl = HelpService.getServerHelpUrlByCategory('group-assets');
  return (
    <div style={{ width: 400 }}>
      <p>{Messages.groupDatabases_moreInfo_context()}</p>
      <ul>
        <li>{Messages.groupDatabases_moreInfo_bullet1()}</li>
        <li>{Messages.groupDatabases_moreInfo_bullet2()}</li>
      </ul>
      <TextLinkWidget
        testId="learn-more-link"
        href={learnMoreDialogUrl}
        target="_blank" rel="noopener noreferrer"
      >
        {Messages.groupDatabases_learnMore()}
      </TextLinkWidget>
    </div>
  );
}


const explanatoryParagraphClass = cssClass('group-databases-dialog', 'explanatory-paragraph', {
  lineHeight: `${TabStyles.Sizing.ListRowHeight}px`,
  marginBottom: TabStyles.Spacing.DefaultSpace,
});

const inlineButtonContainerClass = cssClass('group-databases-dialog', 'inline-button-container', {
  ...boxStyle,
  marginLeft: TabStyles.Spacing.SmallSpace,
  height: TabStyles.Sizing.ListRowHeight,
});

const scrollableRadioButtonsAreaClass = cssClass('group-databases-dialog', 'scrollable-radio-buttons-area', {
  marginTop: TabStyles.Spacing.MediumSpace,
  paddingRight: TabStyles.Spacing.LargeSpace, // Accommodate possible scroll bar
  maxHeight: 312, // Arbitrarily-chosen height that looks good (15.6 items)
  overflowY: 'auto',
});

const radioButtonRowClass = cssClass('group-databases-dialog', 'radio-button-row', {
  alignItems: 'center',
});

const radioButtonRowSpacerClass = cssClass('group-databases-dialog', 'radio-button-row-spacer', {
  display: 'inline-block',
  width: TabStyles.Spacing.SmallSpace,
});

const externalLinkIconClass = cssClass('group-databases-dialog', 'external-link-icon', {
  height: 16, // Empirically-chosen height and width so it looks good
  width: 16,
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
});
